import { type RespondedQuestionnaireAnswers } from '@aikoa/common'
import { languages, type Language } from '@aikoa/i18n'
import { useTolgee, useTranslations } from '@aikoa/i18n/react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { appHeaderState$ } from '~/components/AppHeader'
import FeedbackAnswersView from '~/components/feedback/FeedbackAnswersView'
import { FeedbackFlow } from '~/components/feedback/FeedbackFlow'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import { ToggleableInterestDisplay } from '~/components/interests/ToggleableInterestDisplay'
import { useAppView } from '~/components/layout/AppViewContext'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { SectionContent } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import RealtorCard from '~/components/realtors/RealtorCard'
import { Label } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { Button, ButtonLabel, LinkButton } from '~/components/ui/button'
import { PolicyLinks } from '~/components/user/PolicyLinks'
import { useUndoFeedbackInactive } from '~/data/feedback/useFeedback'
import { useInterest } from '~/data/interests/useInterests'
import { ConfettiTrigger } from '~/lib/ConfettiProvider'
import { trpc } from '~/lib/trpc'

const FEEDBACK_NONCES_STORAGE_KEY = 'feedbackState:nonces'

function parseStoredNonces(rawValue: string): Record<string, string | null> {
  try {
    const parsedValue: unknown = JSON.parse(rawValue)

    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return {}
    }

    const parsedObject = parsedValue as Record<string, unknown>
    const validatedNonces: Record<string, string | null> = {}

    for (const [feedbackIdentifier, parsedNonce] of Object.entries(parsedObject)) {
      if (typeof parsedNonce === 'string' || parsedNonce === null) {
        validatedNonces[feedbackIdentifier] = parsedNonce
      }
    }

    return validatedNonces
  } catch {
    return {}
  }
}

function getInitialNoncesByFeedbackId(): Record<string, string | null> {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const storedValue = window.localStorage.getItem(FEEDBACK_NONCES_STORAGE_KEY)

    if (!storedValue) {
      return {}
    }

    return parseStoredNonces(storedValue)
  } catch {
    return {}
  }
}

function Feedback() {
  const { setBackgroundTheme } = useAppView()

  useEffect(() => {
    setBackgroundTheme(0)
  }, [setBackgroundTheme])

  useEffect(() => {
    appHeaderState$.restrictedStates.set(() => new Set(['back', 'menu', 'onboarding']))

    return () => {
      appHeaderState$.restrictedStates.clear()
    }
  }, [])

  return (
    <ScreenView>
      <FeedbackContent />
    </ScreenView>
  )
}

export default Feedback

function FeedbackContent() {
  const { t } = useTranslations('feedback')
  const { feedbackId } = useLocalSearchParams<{
    feedbackId?: string
  }>()

  const [noncesByFeedbackId, setNoncesByFeedbackId] = useState<Record<string, string | null>>(
    getInitialNoncesByFeedbackId
  )
  const nonce = feedbackId ? noncesByFeedbackId[feedbackId] : undefined

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(FEEDBACK_NONCES_STORAGE_KEY, JSON.stringify(noncesByFeedbackId))
    } catch {
      return
    }
  }, [noncesByFeedbackId])

  const [completionStatus, setCompletionStatus] = useState<
    'completed' | 'declined' | 'inactive' | undefined
  >(undefined)
  const { showAlert } = useAlert()

  const {
    data: surveyData,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    trpc.feedback.feedbackSurveyData.queryOptions(
      {
        feedbackId: feedbackId ?? '',
        nonce: nonce || undefined,
      },
      {
        enabled: !!feedbackId,
        placeholderData: keepPreviousData,
      }
    )
  )

  const surveyResponseData = surveyData && 'data' in surveyData ? surveyData.data : null
  const undoFeedbackInactiveMutation = useUndoFeedbackInactive()

  useEffect(() => {
    if (!feedbackId || !surveyResponseData?.survey_nonce) {
      return
    }

    setNoncesByFeedbackId((currentNoncesByFeedbackId) => {
      if (currentNoncesByFeedbackId[feedbackId] === surveyResponseData.survey_nonce) {
        return currentNoncesByFeedbackId
      }

      return {
        ...currentNoncesByFeedbackId,
        [feedbackId]: surveyResponseData.survey_nonce,
      }
    })
  }, [feedbackId, surveyResponseData?.survey_nonce])

  const { data: interest } = useInterest()

  useEffect(() => {
    if (surveyResponseData?.completed_at) {
      setCompletionStatus('completed')
      return
    }

    if (surveyResponseData?.contact.disabled_reason === 'INACTIVE') {
      setCompletionStatus('inactive')
    }
  }, [surveyResponseData?.completed_at, surveyResponseData?.contact.disabled_reason])

  const tolgee = useTolgee()

  useEffect(() => {
    if (surveyResponseData?.contact.language) {
      const contactLanguage = surveyResponseData.contact.language

      if (languages.includes(contactLanguage as Language)) {
        void tolgee.changeLanguage(contactLanguage)
      }
    }
  }, [surveyResponseData?.contact.language, tolgee])

  const responseAnswers = surveyResponseData?.interest?.answers ?? null
  const interestData = surveyResponseData?.interest
  const isGeneralError = isError || (!isLoading && !surveyData && !!feedbackId)

  const dataError =
    surveyData &&
    'error' in surveyData &&
    surveyData.error &&
    surveyData.error !== 'ALREADY_ANSWERED'
      ? surveyData.error
      : null

  const realtorUserId = surveyResponseData?.realtor_user_id
    ? surveyResponseData.realtor_user_id
    : surveyData && 'realtor_user_id' in surveyData && surveyData.realtor_user_id
      ? surveyData.realtor_user_id
      : null

  const shouldShowBlockingError = (isGeneralError || dataError) && !surveyResponseData
  const shouldShowAlreadyAnswered =
    surveyData &&
    'error' in surveyData &&
    surveyData.error === 'ALREADY_ANSWERED' &&
    !surveyResponseData

  return (
    <>
      {isLoading && <LoadingView />}
      {shouldShowBlockingError && (
        <ContentBox contentContainerClassName="gap-6">
          <Alert variant="destructive">
            <AlertTitle variant="destructive">{t(($) => $.errors.title)}</AlertTitle>
            {isGeneralError ? (
              <AlertDescription variant="destructive">
                {t(($) => $.errors.loadFailed)}
              </AlertDescription>
            ) : (
              <AlertDescription variant="destructive">
                {dataError === 'INVALID_NONCE'
                  ? t(($) => $.errors.invalidNonce)
                  : dataError === 'EXPIRED'
                    ? t(($) => $.errors.expired)
                    : t(($) => $.errors.loadFailedGeneric)}
              </AlertDescription>
            )}
          </Alert>
          <LinkButton href="/" variant="primary">
            <ButtonLabel>{t(($) => $.flow.back)}</ButtonLabel>
          </LinkButton>
          {realtorUserId && (
            <View className="mt-8 items-center gap-4 self-stretch rounded-lg bg-muted p-4 px-6 text-center">
              <Label className="text-center font-light text-sm">
                {t(($) => $.contact.realtorContact)}
              </Label>
              <RealtorCard userId={realtorUserId} />
            </View>
          )}
        </ContentBox>
      )}

      {shouldShowAlreadyAnswered ? (
        <View testID="feedback-already-answered-state" className="flex-1">
          <ContentBox>
          <Heading2 className="mb-4 text-center font-bold text-3xl">
            {t(($) => $.success.title)}
          </Heading2>
          <SectionContent>{t(($) => $.success.alreadyAnswered)}</SectionContent>
          {realtorUserId && (
            <View className="mt-8 items-center gap-4 self-stretch rounded-lg bg-muted p-4 px-6 text-center">
              <Label className="text-center font-light text-sm">
                {t(($) => $.contact.realtorContact)}
              </Label>
              <RealtorCard userId={realtorUserId} showContactInfo />
            </View>
          )}
          {responseAnswers ? (
            <View className="mt-8 gap-4">
              <Heading3 className="mb-1 text-center font-normal">
                {t(($) => $.answers.title)}
              </Heading3>
              <FeedbackAnswersView
                answers={responseAnswers as RespondedQuestionnaireAnswers}
                answersClassName="rounded-content w-full bg-muted py-2"
                itemClassName="px-6"
              />
              {interestData && (
                <>
                  <Heading3 className="mt-4 text-center font-normal">
                    {t(($) => $.answers.searchTitle)}
                  </Heading3>
                  <ToggleableInterestDisplay interest={interestData} className="mb-2" />
                </>
              )}
            </View>
          ) : null}
          </ContentBox>
        </View>
      ) : completionStatus === 'completed' ? (
        <View testID="feedback-completed-state" className="flex-1">
          <ContentBox contentContainerClassName="relative">
          <ConfettiTrigger
            confettiKey="feedback-completed"
            className="mb-6 size-[64px] items-center self-center">
            <Icon name="checkmark-circle" size={64} className="text-secondary" />
          </ConfettiTrigger>
          <Heading2 className="mb-6 text-center font-bold text-3xl">
            {t(($) => $.success.title)}
          </Heading2>
          <View className="mb-4 gap-4">
            <SectionContent textClassName="text-pretty text-left text-lg/normal">
              {t(($) => $.success.submissionComplete)}
            </SectionContent>
            <SectionContent textClassName="text-pretty text-left text-base/normal">
              {t(($) => $.success.submissionHelpful)}
            </SectionContent>
            <SectionContent textClassName="text-pretty text-left text-base/normal">
              {t(($) => $.success.buyerServiceDescription)}
            </SectionContent>
            <SectionContent textClassName="text-pretty text-left text-base/normal">
              {t(($) => $.success.additionalFeedback)}{' '}
            </SectionContent>
            <LinkButton
              href="mailto:support@aikoa.ai"
              variant="link"
              className="self-start px-0 py-1">
              <ButtonLabel>{t(($) => $.success.supportEmail)}</ButtonLabel>
            </LinkButton>
          </View>
          {responseAnswers ? (
            <View className="mt-8 gap-4">
              <Heading3 className="mb-1 text-center font-normal">
                {t(($) => $.answers.title)}
              </Heading3>
              <FeedbackAnswersView
                className="w-full"
                answers={responseAnswers as RespondedQuestionnaireAnswers}
                answersClassName="rounded-content w-full bg-muted py-2"
                itemClassName="px-6"
              />
              {interestData && (
                <>
                  <Heading3 className="mt-4 text-center font-normal">
                    {t(($) => $.answers.searchTitle)}
                  </Heading3>
                  <ToggleableInterestDisplay interest={interestData} className="mb-2" />
                </>
              )}
            </View>
          ) : (
            <LoadingView />
          )}
          </ContentBox>
        </View>
      ) : completionStatus === 'inactive' && surveyResponseData ? (
        <View testID="feedback-inactive-state" className="flex-1">
          <ContentBox contentContainerClassName="relative gap-6">
          <View className="items-center self-center">
            <Icon name="ban" size={42} className="text-destructive" />
          </View>
          <View className="gap-6">
            <Heading2 className="text-center font-medium text-2xl">
              {t(($) => $.inactiveState.title)}
            </Heading2>
            <SectionContent>{t(($) => $.inactiveState.description)}</SectionContent>
            <Button
              variant="primary"
              disabled={undoFeedbackInactiveMutation.isPending}
              onPress={() => {
                if (!surveyResponseData.survey_nonce) {
                  showAlert({
                    type: 'error',
                    title: t(($) => $.errors.title),
                    description: t(($) => $.errors.invalidNonce),
                  })
                  return
                }

                undoFeedbackInactiveMutation.mutate(
                  {
                    feedbackId: surveyResponseData.id,
                    nonce: surveyResponseData.survey_nonce,
                  },
                  {
                    onSuccess: (result) => {
                      if (!result.success) {
                        showAlert({
                          type: 'error',
                          title: t(($) => $.errors.title),
                          description: t(($) => $.errors.submissionFailed),
                        })
                        return
                      }

                      setCompletionStatus(undefined)
                      void refetch()
                    },
                    onError: () => {
                      showAlert({
                        type: 'error',
                        title: t(($) => $.errors.title),
                        description: t(($) => $.errors.submissionFailedConnection),
                      })
                    },
                  }
                )
              }}>
              <ButtonLabel>{t(($) => $.inactiveState.undoButton)}</ButtonLabel>
            </Button>
            {realtorUserId && (
              <RealtorCard userId={realtorUserId} showContactInfo className="mx-auto mt-6" />
            )}
          </View>
          </ContentBox>
        </View>
      ) : completionStatus === 'declined' || surveyResponseData?.declined ? (
        <ContentBox contentContainerClassName="relative">
          <View className="items-center self-center">
            <Icon name="x-circle" size={42} className="mb-6 text-neutral-500" />
          </View>
          <View className="gap-6">
            <Heading2 className="text-2 text-center font-medium">
              {t(($) => $.declined.title)}
            </Heading2>
            <SectionContent>{t(($) => $.declined.description)}</SectionContent>
            {realtorUserId && <RealtorCard userId={realtorUserId} showContactInfo />}
          </View>
        </ContentBox>
      ) : surveyResponseData ? (
        <FeedbackFlow
          flowId={`feedback_${surveyResponseData.id}`}
          feedbackRequest={surveyResponseData}
          initialInterestData={surveyResponseData.interest ?? interest?.data ?? null}
          initialContactDetails={{
            name: surveyResponseData.contact.name,
            email: surveyResponseData.contact.email ?? undefined,
            phone: surveyResponseData.contact.phone ?? undefined,
            language: (surveyResponseData.contact.language as 'fi' | 'en' | 'sv' | 'es') ?? 'fi',
          }}
          onClose={() => {
            setCompletionStatus('declined')
            void refetch()
          }}
          onComplete={async (success) => {
            if (success === 'completed') {
              setCompletionStatus('completed')
              void refetch()
            }

            if (success === 'inactive') {
              setCompletionStatus('inactive')
              void refetch()
            }
          }}
        />
      ) : null}
      <PolicyLinks theme="dark" />
    </>
  )
}
